import type { ReactNode } from 'react';
import { SidebarInset, SidebarProvider } from '@/shared/ui/sidebar';
import { AppSidebar } from './AppSidebar';
import { MobileHeader } from './MobileHeader';
import { PageHeader } from './PageHeader';

type AppLayoutProps = {
  title?: ReactNode;
  actions?: ReactNode;
  banners?: ReactNode;
  footer?: ReactNode;
  /** Custom sidebar component. Defaults to AppSidebar. */
  sidebar?: ReactNode;
  /** Sidebar width CSS value. Defaults to '262px'. */
  sidebarWidth?: string;
  children?: ReactNode;
};

/**
 * Read the persisted sidebar collapse state from the cookie that
 * `SidebarProvider` writes on every toggle (see `SIDEBAR_COOKIE_NAME`
 * in `shared/ui/sidebar.tsx`). We read it here because the shared
 * provider only writes the cookie — it does not rehydrate from it on
 * mount. If shadcn upstream gains a built-in lazy initial-state read,
 * fold this into `SidebarProvider` so consumers don't need to know
 * the cookie name.
 */
function readSidebarCookie(): boolean {
  if (typeof document === 'undefined') return true;
  const match = document.cookie.match(/(?:^|;\s*)sidebar_state=(\w+)/);
  return match ? match[1] === 'true' : true;
}

const sidebarInsetStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
};

const mainStyle: React.CSSProperties = {
  maxWidth: 1600,
  margin: '0 auto',
  width: '100%',
  flex: '1 1 0%',
  overflowX: 'auto',
  padding: '16px 32px 32px',
};

function buildSidebarProviderStyle(
  sidebarWidth: string,
): React.CSSProperties {
  return { '--sidebar-width': sidebarWidth } as React.CSSProperties;
}

export function AppLayout({
  title,
  actions,
  banners,
  footer,
  sidebar,
  sidebarWidth = '262px',
  children,
}: AppLayoutProps) {
  return (
    <div className="lms-modern-chrome min-h-screen bg-background text-foreground">
      <SidebarProvider
        defaultOpen={sidebar ? true : readSidebarCookie()}
        style={buildSidebarProviderStyle(sidebarWidth)}
      >
        {sidebar ?? <AppSidebar />}
        <SidebarInset style={sidebarInsetStyle}>
          {/* Mobile header — always visible on small screens */}
          <MobileHeader />

          {/* Page header — always visible */}
          <PageHeader title={title} actions={<>{actions}{banners}</>} />

          {/* Main content — constrained, with matching top/bottom padding */}
          <main style={mainStyle}>{children}</main>

          {/* Footer — neutralise legacy negative horizontal margins */}
          <div className="lms-footer-wrap">{footer}</div>
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
}
